import axios from 'axios';

import {
    ACTIVE_BLOCKCHAINS,
    ACTIVE_BLOCKCHAINS_BALOR,
    DEPOSIT_ALL,
    DEPOSIT_FAIL,
    POST_DATA_GRAPH,
    POST_REFUEL_DEPOSIT,
    POST_REFUND_DEPOSIT,
    POST_REFUND_WITHDRAW,
    POST_RETRY_DEPOSIT,
    SERVER_CHALLENGE_URL,
    SIGN_IN_URL,
    TRANSACTIONS_URL,
    WITHDRAW_ALL,
    WITHDRAW_FAIL,
    WITHDRAW_REFUNDED,
} from '../consts';
import ServerChallengeModel from '../models/server-challenge';
import AuthCookies from '../components/auth-cookies';
import ResponseModel from '../models/ResponseModel';

export default class Api {

    private static getAuthHeader() {
        const {getAuthCookie} = AuthCookies();
        return {headers: {Authorization: `Bearer ${getAuthCookie()}`}};
    }

    public static async getActiveBlockchains(balor: boolean = false):
        Promise<any> {
        try {
            const url = balor ? (ACTIVE_BLOCKCHAINS_BALOR || "") : ACTIVE_BLOCKCHAINS;
            const response = await axios.get(url, Api.getAuthHeader());
            console.log(response.data);
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getMetaData(uri: string) {
        try {
            const response = await axios.get(uri, Api.getAuthHeader());
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async refundWithdraw(spendTx: string, payTx: string, refund: boolean): Promise<any> {
        try {
            const {getAuthCookie} = AuthCookies();
            const data = `spendTx=${encodeURIComponent(spendTx || '')}&payTx=${encodeURIComponent(payTx || '')}&refund=${(+refund)}`;
            const response = await axios.post(
                POST_REFUND_WITHDRAW,
                data,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: `Bearer ${getAuthCookie()}`,
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async refundDeposit(userAddress: string, txHash: string, blockchain: string):
        Promise<any> {
        try {
            const {getAuthCookie} = AuthCookies();
            const data = `userAddress=${encodeURIComponent(userAddress || '')}&txHash=${encodeURIComponent(txHash || '')}&blockchain=${encodeURIComponent(blockchain || '')}`;
            const response = await axios.post(
                POST_REFUND_DEPOSIT,
                data,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: `Bearer ${getAuthCookie()}`,
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async retryDeposit(txHash: string, blockchain: string):
        Promise<any> {
        try {
            const {getAuthCookie} = AuthCookies();
            const data = `txHash=${encodeURIComponent(txHash || '')}&blockchain=${encodeURIComponent(blockchain || '')}`;
            const response = await axios.post(
                POST_RETRY_DEPOSIT,
                data,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: `Bearer ${getAuthCookie()}`,
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async startDeposit(depositAddress: string, txHash: string, blockchain: string):
        Promise<any> {
        try {
            const {getAuthCookie} = AuthCookies();
            const data = `depositAddress=${encodeURIComponent(depositAddress || '')}&blockchain=${encodeURIComponent(blockchain || '')}&txHash=${encodeURIComponent(txHash || '')}`;
            const response = await axios.post(
                POST_REFUEL_DEPOSIT,
                data,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        Authorization: `Bearer ${getAuthCookie()}`,
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getWithdraw(status: string, limit: number, offset: number):
        Promise<ResponseModel> {
        try {
            let url = '';
            switch (status) {
                case 'failed':
                    url = `${WITHDRAW_FAIL}?limit=${limit}&offset=${offset}`;
                    break;
                case 'refunded':
                    url = `${WITHDRAW_REFUNDED}?limit=${limit}&offset=${offset}`;
                    break;
                case 'all':
                default:
                    url = `${WITHDRAW_ALL}?limit=${limit}&offset=${offset}`;
                    break;
            }

            const response = await axios.get(url, Api.getAuthHeader());
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getDeposit(blockchain: string, status: string, limit: number, offset: number):
        Promise<any> {
        try {
            let url = '';
            switch (status) {
                case 'failed':
                    url = `${DEPOSIT_FAIL}/${blockchain}?limit=${limit}&offset=${offset}`;
                    break;
                case 'all':
                default:
                    url = `${DEPOSIT_ALL}/${blockchain}?limit=${limit}&offset=${offset}`;
                    break;
            }
            const response = await axios.get(url, Api.getAuthHeader());
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getServerChallenge(address: string): Promise<ServerChallengeModel> {
        try {
            const response = await axios.get(`${SERVER_CHALLENGE_URL + address}`, Api.getAuthHeader());
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getToken(message: string, signature: string, pin: string, address: string):
        Promise<string> {
        try {
            const data = `message=${encodeURIComponent(message)}&signature=${encodeURIComponent(signature)}`;
            const response = await axios.post(
                `${SIGN_IN_URL}?pin=${pin}&json=true&loginBySign=${address}`,
                data,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getDataGraph(graphDefinition: any) {
        try {
            const {getAuthCookie} = AuthCookies();
            const response = await axios.post(
                POST_DATA_GRAPH,
                JSON.stringify(graphDefinition),
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${getAuthCookie()}`,
                    },
                },
            );
            return response.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

    public static async getTransactions(limit: number, offset: number): Promise<any> {
        try {
            const response = await axios.get(TRANSACTIONS_URL + "?limit=" + limit + "&offset=" + offset);
            return response.data.data;
        } catch (e) {
            console.error(e);
            throw e;
        }
    }

}
